/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 * HandleExportApp is a custom hook-like component that manages the logic for exporting
 * data into PDF or Excel formats. It handles multiple export scenarios including:
 * - Standard exports
 * - Custom exports with contractor/supplier filtering
 * - Attendance-specific exports with timezone and key support
 *
 * It ensures validation of selected headers, builds the request body dynamically,
 * dispatches Redux async actions, and handles user feedback via toast notifications.
 *
 * @param {Function} [clearSelectedOptions=() => {}] - Callback to reset selected headers after export.
 * @param {Array} selectedOptions - List of headers selected by the user for export (must have at least 2).
 * @param {Object} body - Base payload used in export requests.
 * @param {boolean} search - Flag to determine if search context should be applied.
 * @param {Function} [onHide=() => {}] - Callback to close modal or UI after export completes.
 * @param {number} moduleId - Unique identifier for the current module.
 * @param {string} option - Export option key (module/model name).
 * @param {Object} exportAttendance - Special payload object used for attendance export.
 * @param {string} searchElement - Extra key for attendance search when search is disabled.
 *
 * @returns {Object} - Object containing handler functions for different export flows:
 *   - handleDownloadPDF
 *   - handleDownloadExcel
 *   - handleDownloadPDFAttendance
 *   - handleDownloadExcelAttendance
 */
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useDispatch } from "react-redux";
import {
    ExportDataExcel, ExportDataExcelCustom,
    ExportDataPDF, ExportDataPDFCustom, ExportExcelSimple, ExportExcelSimpleCustom,
    exportPDFSimple, ExportPDFSimpleCustom
} from "../../../reduxToolkit/Files/FilesApi";
import { toast } from "react-toastify";
import getTimezoneOffset from "../../../utils/GetTimeZone";
import {errorMsg} from "../../../Apis/functions";

const HandleExportApp = ({ clearSelectedOptions = () => {}, selectedOptions, body, search, onHide = () => {}, moduleId, option, exportAttendance, searchElement, contractorSupplier=true }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    // Session storage values used for custom export filtering
    const [contractorId] = useState(contractorSupplier ? sessionStorage.getItem('contractorId'): null);
    const [supplierId] = useState(contractorSupplier ? sessionStorage.getItem('providerId'): null);

    /**
     * Handles PDF export with multiple conditions:
     * - Requires at least two selected headers.
     * - Decides between simple export, module-based export, or contractor/supplier custom export.
     */
    const handleDownloadPDF = () => {
        if (selectedOptions.length < 2) {
            toast.warn(t('select_at_least_two_fields'));
            return;
        }

        let bodyToSend = { ...body, headers: selectedOptions };

        if (search) {
            // CASE 1: No contractor/supplier set
            if (contractorId === null && supplierId === null) {
                dispatch(ExportDataPDF({ option, moduleId, body: bodyToSend }))
                    .then(({ payload: { status } }) => {
                        if (status === 200) {
                            toast.success(t('file_generation_in_progress').toUpperCase());
                            clearSelectedOptions();
                            onHide();
                        } else {
                            toast.error(t('something_went_wrong_2'));
                        }
                    })
                    .catch(() => toast.error(t('something_went_wrong_2')));
            }
            // CASE 2: Custom contractor/supplier export
            else {
                const updatedBody = { ...bodyToSend, userToFindId: contractorId || supplierId };
                dispatch(ExportDataPDFCustom({ option, body: updatedBody, moduleId }))
                    .then(({ payload: { status } }) => {
                        if (status === 200) {
                            toast.success(t('file_generation_in_progress').toUpperCase());
                            clearSelectedOptions();
                            onHide();
                        } else {
                            toast.error(t('something_went_wrong_2'));
                        }
                    })
                    .catch(() => toast.error(t('something_went_wrong_2')));
            }
        } else {
            // CASE 3: Simple export when no search applied
            if (contractorId === null && supplierId === null) {
                dispatch(exportPDFSimple({ body: bodyToSend, moduleId }))
                    .then(({ payload: { status } }) => {
                        if (status === 200) {
                            toast.success(t('file_generation_in_progress').toUpperCase());
                            clearSelectedOptions();
                            onHide();
                        } else {
                            toast.error(t('something_went_wrong_2'));
                        }
                    })
                    .catch(() => toast.error(t('something_went_wrong_2')));
            }
            // CASE 4: Simple custom export with contractor/supplier
            else {
                const bodyWithId = {
                    ...body,
                    headers: selectedOptions,
                    model: option,
                    userId: contractorId ?? supplierId
                };

                dispatch(ExportPDFSimpleCustom({ body: bodyWithId, moduleId }))
                    .then(({ payload: { status } }) => {
                        if (status === 200) {
                            toast.success(t('file_generation_in_progress').toUpperCase());
                            clearSelectedOptions();
                            onHide();
                        } else {
                            toast.error(t('something_went_wrong_2'));
                        }
                    })
                    .catch(() => toast.error(t('something_went_wrong_2')));
            }
        }
    };

    /**
     * Handles Excel export with logic similar to handleDownloadPDF.
     */
    const handleDownloadExcel = () => {
        if (selectedOptions.length < 2) {
            toast.warn(t('select_at_least_two_fields'));
            return;
        }

        let bodyToSend = { ...body, headers: selectedOptions };

        if (search) {
            if (contractorId === null && supplierId === null) {
                dispatch(ExportDataExcel({ option, body: bodyToSend, moduleId }))
                    .then(({ payload: { status } }) => {
                        handleResponse(status);
                    })
                    .catch(() => toast.error(t('something_went_wrong_2')));
            } else {
                const updatedBody = { ...bodyToSend, userToFindId: contractorId || supplierId };
                dispatch(ExportDataExcelCustom({ option, body: updatedBody, moduleId }))
                    .then(({ payload: { status } }) => {
                        handleResponse(status);
                    })
                    .catch(() => toast.error(t('something_went_wrong_2')));
            }
        } else {
            if (contractorId === null && supplierId === null) {
                dispatch(ExportExcelSimple({ body: bodyToSend, moduleId }))
                    .then(({ payload: { status } }) => {
                        handleResponse(status);
                    })
                    .catch(() => toast.error(t('something_went_wrong_2')));
            } else {
                const bodyWithId = {
                    ...body,
                    headers: selectedOptions,
                    model: option,
                    userId: contractorId ?? supplierId
                };

                dispatch(ExportExcelSimpleCustom({ body: bodyWithId, moduleId }))
                    .then(({ payload: { status } }) => {
                        handleResponse(status);
                    })
                    .catch(() => toast.error(t('something_went_wrong_2')));
            }
        }
    };

    const handleResponse = (status) => {
        if (status === 200) {
            toast.success(t('file_generation_in_progress').toUpperCase());
            clearSelectedOptions();
            onHide();
        } else {
            toast.error(t('something_went_wrong_2'));
        }
    }

    /**
     * Handles Attendance-specific PDF export.
     * - Requires timezone info and extra key if search is disabled.
     * - Uses attendance-specific body when search is enabled.
     */
    const handleDownloadPDFAttendance = () => {
        if (selectedOptions.length < 2) {
            toast.warn(t('select_at_least_two_fields'));
            return;
        }

        if (!search) {
            let bodyToSend = {
                ...body,
                headers: selectedOptions,
                attendanceTimeZone: getTimezoneOffset(),
                model: option,
                key: searchElement
            };

            dispatch(exportPDFSimple({ body: bodyToSend, moduleId }))
                .then(({ payload: { status, data } }) => {
                    if (status === 200) {
                        toast.success(t('file_generation_in_progress').toUpperCase());
                        clearSelectedOptions();
                        onHide();
                    } else errorMsg(data.code);
                });
            return;
        }

        let bodyToSendAdvance = { ...exportAttendance, headers: selectedOptions };

        dispatch(ExportDataPDF({ body: bodyToSendAdvance, option: 'attendance', moduleId }))
            .then(({ payload: { status, data } }) => {
                if (status === 200) {
                    toast.success(t('file_generation_in_progress').toUpperCase());
                    clearSelectedOptions();
                    onHide();
                } else errorMsg(data.code);
            });
    };

    /**
     * Handles Attendance-specific Excel export.
     * - Same logic as PDF but dispatches Excel actions.
     */
    const handleDownloadExcelAttendance = () => {
        if (selectedOptions.length < 2) {
            toast.warn(t('select_at_least_two_fields'));
            return;
        }

        if (!search) {
            let bodyToSend = {
                ...body,
                headers: selectedOptions,
                attendanceTimeZone: getTimezoneOffset(),
                model: option,
                key: searchElement
            };

            dispatch(ExportExcelSimple({ body: bodyToSend, moduleId }))
                .then(({ payload: { status, data } }) => {
                    if (status === 200) {
                        toast.success(t('file_generation_in_progress').toUpperCase());
                        clearSelectedOptions();
                        onHide();
                    } else errorMsg(data.code);
                });
            return;
        }

        let bodyToSendAdvance = { ...exportAttendance, headers: selectedOptions };

        dispatch(ExportDataExcel({ body: bodyToSendAdvance, option: 'attendance', moduleId }))
            .then(({ payload: { status, data } }) => {
                if (status === 200) {
                    toast.success(t('file_generation_in_progress').toUpperCase());
                    clearSelectedOptions();
                    onHide();
                } else errorMsg(data.code);
            });
    };

    // Return all export handlers to be used by the consumer component
    return {
        handleDownloadExcelAttendance,
        handleDownloadExcel,
        handleDownloadPDFAttendance,
        handleDownloadPDF
    };
};

export default HandleExportApp;
